import type { Match } from "navigo";
import type { ChildDom, State } from "vanjs-core";
import van from "vanjs-core";
import { config } from "../app-env/common";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { relativeTimeString } from "../locale";
import {
	addBlurListener,
	addFocusListener,
	removeBlurListener,
	removeFocusListener,
} from "../mobile";
import type { Route, RouteContext } from "../router";
import { centeredColumn } from "../van/centered-column";
import {
	button,
	div,
	input,
	label,
	option,
	p,
	select,
	span,
	table,
	tbody,
	td,
	th,
	tr,
} from "../van/van-tags";
import type { ViewComponent } from "../van/view-component";

class CommandBox implements ViewComponent {
	#cmdHasEnabledField(): boolean {
		switch (this.#command.val) {
			case 0xa1:
			case 0xa3:
			case 0xa6:
				return true;
			default:
				return false;
		}
	}

	#cmdHasRangeFields(): boolean {
		return this.#command.val === 0xa6;
	}

	#cmdHasFwUrlField(): boolean {
		return this.#command.val === 0xac;
	}

	#disableSendButton(): boolean {
		if (this.#imei.val === "") return true;

		if (!this.#cmdHasRangeFields()) {
			if (!(this.#lower.val < this.#upper.val)) return true;
			if (!(this.#lower.val < 4000)) return true;
			if (!(this.#upper.val > 0)) return true;
		}

		return false;
	}

	#newCmdData(): CmdData {
		const cmdData = {} as CmdData;

		if (this.#cmdHasEnabledField()) {
			cmdData.Enabled = this.#enabled.val;
		}

		if (this.#cmdHasRangeFields()) {
			cmdData.Lower = this.#lower.val;
			cmdData.Upper = this.#upper.val;
		}

		if (this.#cmdHasFwUrlField()) {
			cmdData.Url = this.#firmwareUrl.val;
		}

		return cmdData;
	}

	#onChangeCommand(e: Event): void {
		if (!(e.target instanceof HTMLSelectElement)) return;
		this.#command.val = parseInt(e.target.value, 16);
	}

	#onChangeEnabled(e: Event): void {
		if (!(e.target instanceof HTMLSelectElement)) return;
		this.#enabled.val = +e.target.value ? 1 : 0;
	}

	#onChangeFirmware(e: Event): void {
		if (!(e.target instanceof HTMLSelectElement)) return;
		const firmware = e.target.value;
		this.#firmwareUrl.val = `https://${config.sp2gwHost}/firmwares/${firmware}`;
	}

	#onInputLower(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		this.#lower.val = +e.target.value;
	}

	#onInputUpper(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		this.#upper.val = +e.target.value;
	}

	async #onClickSend(): Promise<void> {
		const req = this.#ctx.api.post(`/smartplug2s/${this.#imei.val}/cmds`);
		req.setJsonBody({
			CmdData: this.#newCmdData(),
			CmdType: this.#command.val,
		});
		req.setSignedInUserAuth();

		errorSection.clear();
		const res = await loadingScreen.waitFor(req.do());
		if (!res.ok) {
			errorSection.setText(await res.text());
		}
	}

	#command = van.state(0xa1);
	#ctx: RouteContext;
	#enabled: State<0 | 1> = van.state(0);
	#firmwares: State<string[]>;
	#firmwareUrl = van.state("");
	#imei: State<string>;
	#lower = van.state(0);
	#upper = van.state(4000);
	ChildDom: ChildDom;

	constructor(
		ctx: RouteContext,
		firmwares: State<string[]>,
		imei: State<string>,
	) {
		this.#ctx = ctx;
		this.#firmwares = firmwares;
		this.#imei = imei;
		this.ChildDom = div(
			{ class: "box" },
			label({ class: "label" }, "Commands"),
			div(
				{ class: "field" },
				div(
					{ class: "control" },
					div(
						{ class: "select" },
						select(
							{
								onchange: (e: Event) =>
									this.#onChangeCommand(e),
								value: () =>
									this.#command.val
										.toString(16)
										.toUpperCase(),
							},
							[
								["A1", "Set output power"],
								["A2", "Get status"],
								["A3", "Set beep"],
								["A6", "Set load alarm"],
								["A9", "Get IMSI"],
								["AA", "Get firmware"],
								["AC", "Set firmware"],
							].map((e) =>
								option({ value: e[0] }, `${e[0]}: ${e[1]}`),
							),
						),
					),
				),
			),
			div(
				{
					class: () =>
						this.#cmdHasEnabledField() ? "field" : "is-hidden",
				},
				div(
					{ class: "control" },
					div(
						{ class: "select" },
						select(
							{
								onchange: (e: Event) =>
									this.#onChangeEnabled(e),
								value: () => this.#enabled.val.toString(),
							},
							option({ value: "0" }, "Off"),
							option({ value: "1" }, "On"),
						),
					),
				),
			),
			div(
				{
					class: () =>
						this.#cmdHasRangeFields() ? "field" : "is-hidden",
				},
				div(
					{ class: "field has-addons" },
					div(
						{ class: "control" },
						input({
							class: "input",
							oninput: (e: Event) => this.#onInputLower(e),
							type: "tel",
							value: () => this.#lower.val.toString(),
						}),
					),
					div(
						{ class: "control" },
						span({ class: "button is-static" }, "–"),
					),
					div(
						{ class: "control" },
						input({
							class: "input",
							oninput: (e: Event) => this.#onInputUpper(e),
							type: "tel",
							value: () => this.#upper.val.toString(),
						}),
					),
					div(
						{ class: "control" },
						span({ class: "button is-static" }, "W"),
					),
				),
			),
			div(
				{
					class: () =>
						this.#cmdHasFwUrlField() ? "field" : "is-hidden",
				},
				div(
					{ class: "control" },
					div({ class: "select" }, () =>
						select(
							{
								onchange: (e: Event) =>
									this.#onChangeFirmware(e),
							},
							this.#firmwares.val.map((fw) =>
								option({ value: fw }, fw),
							),
						),
					),
				),
			),
			div(
				{ class: "buttons" },
				button(
					{
						class: "button is-primary",
						disabled: () => this.#disableSendButton(),
						onclick: () => this.#onClickSend(),
					},
					"Send",
				),
			),
		);
	}
}

class DeviceStatusBox implements ViewComponent {
	async #getLastStatus(): Promise<void> {
		if (this.#imei.val.length !== 15) return;

		const req = this.#ctx.api.get(
			`/smartplug2s/${this.#imei.val}/last-status`,
		);
		req.setSignedInUserAuth();

		const res = await req.do();
		if (res.ok) {
			this.#lastStatus.val = await res.json();
		}
	}

	#onBlur(): void {
		clearInterval(this.#statusInterval);
	}

	#onFocus(): void {
		this.refresh();
	}

	onLeave(): void {
		clearInterval(this.#statusInterval);
		removeBlurListener(this.#listenerKey);
		removeFocusListener(this.#listenerKey);
	}

	async refresh(): Promise<void> {
		clearInterval(this.#statusInterval);
		this.#statusInterval = setInterval(() => this.#getLastStatus(), 5e3);
		await this.#getLastStatus();
	}

	#ctx: RouteContext;
	#imei: State<string>;
	#lastStatus = van.state({
		Consumption: 0,
		Load: 0,
		SignalStrength: 0,
		Timestamp: "",
	});
	#listenerKey = "sp2-factory-tool-device-status-box";
	#statusInterval: NodeJS.Timeout | undefined;
	ChildDom: ChildDom;

	constructor(ctx: RouteContext, imei: State<string>) {
		this.#ctx = ctx;
		this.#imei = imei;
		this.ChildDom = div(
			{ class: "box" },
			label({ class: "label" }, "Device status"),
			table(
				{ class: "table is-fullwidth is-narrow is-striped" },
				tbody(
					tr(
						th("Consumption"),
						td(() => `${this.#lastStatus.val.Consumption} Wh`),
					),
					tr(
						th("Load"),
						td(() => `${this.#lastStatus.val.Load} W`),
					),
					tr(
						th("Signal strength"),
						td(() => `${this.#lastStatus.val.SignalStrength}%`),
					),
					tr(
						th("Timestamp"),
						td(() =>
							relativeTimeString(this.#lastStatus.val.Timestamp),
						),
					),
				),
			),
		);

		addBlurListener(this.#listenerKey, () => this.#onBlur());
		addFocusListener(this.#listenerKey, () => this.#onFocus());
	}
}

class FirmwareBox implements ViewComponent {
	#onChangeSelectedFile(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		if (!e.target.files?.length) return;
		if (e.target.files.length < 1) return;
		this.#selectedFile.val = e.target.files[0];
	}

	async #uploadFile(): Promise<void> {
		errorSection.clear();

		if (!this.#selectedFile.val) {
			errorSection.setText("no file selected");
			return;
		}

		const req = this.#ctx.api.put(
			`/firmwares/smartplug2/${this.#selectedFile.val.name}`,
		);
		req.setSignedInUserAuth();
		req.setBody(this.#selectedFile.val);

		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#firmwares.val = await res.json();
		} else {
			errorSection.setText(await res.text());
		}
	}

	#ctx: RouteContext;
	#firmwares: State<string[]>;
	#selectedFile: State<File | null> = van.state(null);
	ChildDom: ChildDom;

	constructor(ctx: RouteContext, firmwares: State<string[]>) {
		this.#ctx = ctx;
		this.#firmwares = firmwares;
		this.ChildDom = div(
			{
				class: "box",
			},
			div(
				{ class: "field" },
				label({ class: "label" }, "Firmwares"),
				() =>
					div(
						{ class: "tags" },
						this.#firmwares.val.map((fw) =>
							span({ class: "tag" }, fw),
						),
					),
				p(
					{ class: "help" },
					`Let's Encrypt CA: https://${config.sp2gwHost}/firmwares/{filename}`,
				),
				p(
					{ class: "help" },
					`Self-signed CA: https://${config.sp2gwSelfSignedHost}/firmwares/{filename}`,
				),
			),
			div(
				{ class: "field" },
				div(
					{ class: "field has-addons" },
					div(
						{ class: "control is-expanded" },
						input({
							class: "input",
							onchange: (e: Event) =>
								this.#onChangeSelectedFile(e),
							type: "file",
						}),
					),
					div(
						{ class: "control" },
						button(
							{
								class: "button is-primary",
								disabled: () => !this.#selectedFile.val,
								onclick: () => this.#uploadFile(),
							},
							"Upload",
						),
					),
				),
			),
		);
	}
}

class ProvisioningBox implements ViewComponent {
	#onClickCopy(): void {
		navigator.clipboard.writeText(this.#mqttPassword.val).catch(() => {
			errorSection.setText("Copy failed");
		});
	}

	async #provision(): Promise<void> {
		errorSection.clear();

		const req = this.#ctx.api.put(
			`/smartplug2s/${this.#imei.val}/provision`,
		);
		req.setSignedInUserAuth();

		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#mqttPassword.val = await res.json();
		} else {
			errorSection.setText(`${res.status}: ${await res.text()}`);
		}
	}

	#ctx: RouteContext;
	#mqttPassword = van.state("");
	#imei: State<string>;
	ChildDom: ChildDom;

	constructor(ctx: RouteContext, imei: State<string>) {
		this.#ctx = ctx;
		this.#imei = imei;
		this.ChildDom = div(
			{ class: "box" },
			label({ class: "label" }, "MQTT password"),
			div(
				{ class: "field" },
				div(
					{ class: "control" },
					span(
						{
							class: "input",
							disabled: true,
						},
						this.#mqttPassword,
					),
				),
			),
			div(
				{ class: "buttons" },
				button(
					{
						class: "button is-primary",
						disabled: () => this.#imei.val === "",
						onclick: () => this.#provision(),
					},
					"Create new",
				),
				button(
					{
						class: "button is-primary",
						disabled: () => this.#mqttPassword.val === "",
						onclick: () => this.#onClickCopy(),
					},
					"Copy password",
				),
			),
		);
	}
}

export class Sp2FactoryPage implements Route {
	async #getFirmwareList(): Promise<void> {
		const req = this.#ctx.api.get("/firmwares/smartplug2");
		req.setSignedInUserAuth();

		errorSection.clear();
		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#firmwares.val = await res.json();
		} else {
			this.#firmwares.val = [];
			errorSection.setText(await res.text());
		}
	}

	#onClickClear(): void {
		this.#imei.val = "";
	}

	#onInputImei(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		this.#imei.val = e.target.value;
	}

	onLeave(): void {
		this.#deviceStatusBox.onLeave();
	}

	async refresh(match: Match): Promise<void> {
		if (match.params?.imei) {
			this.#imei.val = match.params.imei;
		}

		await this.#getFirmwareList();
		this.#deviceStatusBox.refresh();
	}

	#ctx: RouteContext;
	#commandBox: CommandBox;
	#deviceStatusBox: DeviceStatusBox;
	#firmwareBox: FirmwareBox;
	#firmwares = van.state([""]);
	#imei = van.state("");
	#provisioningBox: ProvisioningBox;
	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.#commandBox = new CommandBox(ctx, this.#firmwares, this.#imei);
		this.#deviceStatusBox = new DeviceStatusBox(ctx, this.#imei);
		this.#firmwareBox = new FirmwareBox(ctx, this.#firmwares);
		this.#provisioningBox = new ProvisioningBox(ctx, this.#imei);
		this.ChildDom = centeredColumn(
			{
				size: "narrow",
			},
			div(
				{ class: "box" },
				div(
					{ class: "field" },
					label({ class: "label" }, "IMEI"),
					div(
						{ class: "field has-addons" },
						div(
							{ class: "control is-expanded" },
							input({
								class: "input",
								oninput: (e: Event) => this.#onInputImei(e),
								type: "text",
								value: this.#imei,
							}),
						),
						div(
							{ class: "control" },
							button(
								{
									class: "button is-primary",
									disabled: () => this.#imei.val === "",
									onclick: () => this.#onClickClear(),
								},
								"Clear",
							),
						),
					),
				),
			),
			this.#provisioningBox.ChildDom,
			this.#deviceStatusBox.ChildDom,
			this.#commandBox.ChildDom,
			this.#firmwareBox.ChildDom,
		);
	}
}

interface CmdData {
	Enabled?: 0 | 1;
	Lower?: number;
	Upper?: number;
	Url?: string;
}
